import { clamp } from '@/lib/dom';
import type { FsNode } from '@/fs/types';

/**
 * Where desktop icons sit.
 *
 * Kept pure and away from the component because there are now three ways an
 * icon can get a position — the grid it flows into, a slot the config pinned
 * it to, and wherever the user dragged it — and the arithmetic for resolving
 * that is worth being able to test on its own.
 *
 * Positions are pixels, not cells, because "Align to Grid" can be turned off:
 * with it on a drop snaps to the nearest cell, and with it off the icon simply
 * stays where it was dropped. One coordinate system covers both.
 */

/** XP's default desktop grid: 75px wide cells, one icon plus two label lines tall. */
export const CELL_W = 75;
export const CELL_H = 76;
export const PAD_X = 8;
export const PAD_Y = 8;

export interface Point {
  x: number;
  y: number;
}

export interface Cell {
  col: number;
  row: number;
}

export interface Slot {
  node: FsNode;
  x: number;
  y: number;
}

/** Pixel positions the user has dragged icons to, keyed by path. */
export type Placements = Readonly<Record<string, Point>>;

export const cellKey = (col: number, row: number): string => `${col},${row}`;

export function colsIn(width: number): number {
  return Math.max(1, Math.floor(Math.max(0, width - PAD_X) / CELL_W));
}

export function rowsIn(height: number): number {
  return Math.max(1, Math.floor(Math.max(0, height - PAD_Y * 2) / CELL_H));
}

export function originOf(cell: Cell): Point {
  return { x: PAD_X + cell.col * CELL_W, y: PAD_Y + cell.row * CELL_H };
}

/** The cell a pixel position belongs to — the one its top-left corner is nearest. */
export function cellAt(point: Point): Cell {
  return {
    col: Math.max(0, Math.round((point.x - PAD_X) / CELL_W)),
    row: Math.max(0, Math.round((point.y - PAD_Y) / CELL_H)),
  };
}

export interface Bounds {
  width: number;
  height: number;
}

/** Keep a free-floating icon fully on the desktop. */
function clampPoint(point: Point, bounds: Bounds): Point {
  return {
    x: clamp(point.x, 0, Math.max(0, bounds.width - CELL_W)),
    y: clamp(point.y, 0, Math.max(0, bounds.height - CELL_H)),
  };
}

function clampCell(cell: Cell, bounds: Bounds): Cell {
  return {
    col: clamp(cell.col, 0, colsIn(bounds.width) - 1),
    row: clamp(cell.row, 0, rowsIn(bounds.height) - 1),
  };
}

/* -------------------------------------------------------------------------- */
/* Laying the desktop out                                                     */
/* -------------------------------------------------------------------------- */

export interface LayoutOptions {
  height: number;
  placed: Placements;
  /** Off under Auto Arrange, which puts every icon back in the flow. */
  honourPlaced: boolean;
  /** Also off under a sort order: a pinned icon would sit out of sequence. */
  honourFixed: boolean;
}

/**
 * Column-first placement: icons fill downwards, then wrap into a new column —
 * which is why the layout has to know how tall the desktop currently is.
 * Dragged icons claim their cell first, then icons the config pinned; the rest
 * flow around both.
 */
export function layoutIcons(nodes: readonly FsNode[], options: LayoutOptions): Slot[] {
  const rows = rowsIn(options.height);
  const taken = new Set<string>();
  const slots: Slot[] = [];
  const flowing: FsNode[] = [];

  for (const node of nodes) {
    const point = options.honourPlaced ? options.placed[node.path] : undefined;
    if (point) {
      const cell = cellAt(point);
      taken.add(cellKey(cell.col, cell.row));
      slots.push({ node, x: point.x, y: point.y });
      continue;
    }

    const pos = options.honourFixed ? node.desktopPos : undefined;
    if (pos && pos.col >= 0 && pos.row >= 0 && !taken.has(cellKey(pos.col, pos.row))) {
      taken.add(cellKey(pos.col, pos.row));
      slots.push({ node, ...originOf(pos) });
      continue;
    }

    flowing.push(node);
  }

  let col = 0;
  let row = 0;
  const advance = () => {
    row += 1;
    if (row >= rows) {
      row = 0;
      col += 1;
    }
  };

  for (const node of flowing) {
    // Terminates: `taken` is finite and `advance` walks into fresh columns.
    while (taken.has(cellKey(col, row))) advance();
    taken.add(cellKey(col, row));
    slots.push({ node, ...originOf({ col, row }) });
    advance();
  }

  // Render in visual order so Tab walks the grid the way the eye does.
  return slots.sort((a, b) => a.x - b.x || a.y - b.y);
}

/* -------------------------------------------------------------------------- */
/* Dropping icons                                                             */
/* -------------------------------------------------------------------------- */

export interface Drop {
  path: string;
  /** Where the icon's top-left was let go, in the icon layer's coordinates. */
  x: number;
  y: number;
}

export interface DropOptions extends Bounds {
  /** Cells held by icons that are not part of this drag. */
  taken: ReadonlySet<string>;
  align: boolean;
}

/**
 * Where dropped icons come to rest.
 *
 * With Align to Grid on, each one takes the cell it was let go over, and any
 * that would collide — with an icon staying put, or with another icon in the
 * same drag — walks down the grid to the next free one. With it off they stay
 * exactly where they were dropped, which is the whole point of the setting.
 */
export function placeDropped(drops: readonly Drop[], options: DropOptions): Placements {
  const out: Record<string, Point> = {};
  if (!options.align) {
    for (const drop of drops) out[drop.path] = clampPoint(drop, options);
    return out;
  }

  const rows = rowsIn(options.height);
  const cols = colsIn(options.width);
  const used = new Set(options.taken);

  for (const drop of drops) {
    let { col, row } = clampCell(cellAt(drop), options);
    let guard = rows * cols;
    while (used.has(cellKey(col, row)) && guard > 0) {
      guard -= 1;
      row += 1;
      if (row >= rows) {
        row = 0;
        col = (col + 1) % cols;
      }
    }
    used.add(cellKey(col, row));
    out[drop.path] = originOf({ col, row });
  }

  return out;
}

/* -------------------------------------------------------------------------- */
/* Dropping icons while Auto Arrange is on                                    */
/* -------------------------------------------------------------------------- */

/** Which place in the flow a point falls in, counting down each column first. */
export function flowIndexAt(point: Point, height: number): number {
  const rows = rowsIn(height);
  const cell = cellAt(point);
  return cell.col * rows + cell.row;
}

/**
 * Auto Arrange refuses to let an icon sit anywhere but in the grid, so a drag
 * under it can only mean one thing: put these icons *here* in the order, and
 * let everything else close up behind them. That is what XP did, and it is the
 * only way the gesture does anything at all while the setting is on.
 */
export function reorder(
  order: readonly string[],
  moving: readonly string[],
  index: number,
): string[] {
  const set = new Set(moving);
  const rest = order.filter((path) => !set.has(path));
  const picked = order.filter((path) => set.has(path));
  // `index` is a place on screen, so it counts slots in the arrangement the
  // drop produces — the icon ends up in the cell it was let go over, and
  // everything else closes up around it.
  const at = clamp(index, 0, rest.length);
  return [...rest.slice(0, at), ...picked, ...rest.slice(at)];
}

/**
 * Every loose icon back onto the nearest free cell — what turning Align to
 * Grid back on does to a desktop somebody has been dropping icons around.
 */
export function snapToGrid(placed: Placements): Placements {
  const out: Record<string, Point> = {};
  const used = new Set<string>();
  for (const [path, point] of Object.entries(placed)) {
    const cell = cellAt(point);
    let row = cell.row;
    while (used.has(cellKey(cell.col, row))) row += 1;
    used.add(cellKey(cell.col, row));
    out[path] = originOf({ col: cell.col, row });
  }
  return out;
}
